<template>
  <div class="login-container" ref="loginContainer">
    <canvas ref="waveCanvas" class="bg-wave-canvas" aria-hidden="true"></canvas>
    <div class="login-card">
      <div class="brand-wrap">
        <img src="/logo.png" alt="JMASysc" class="brand-logo">
      </div>
      <div class="logo-section">
        <p class="slogan">Transformando ideias em inovações.</p>
      </div>

      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="usuario">Email</label>
          <input 
            type="email" 
            id="usuario" 
            v-model="credentials.usuario" 
            required
            placeholder="Digite seu email"
          >
        </div>
        
        <div class="form-group">
          <label for="senha">Senha</label>
          <input 
            type="password" 
            id="senha" 
            v-model="credentials.senha" 
            required
            placeholder="Digite sua senha"
          >
        </div>
        
        <div class="checkbox-group">
          <input type="checkbox" id="salvarUsuario" v-model="salvarUsuario">
          <label for="salvarUsuario">Salvar usuário</label>
        </div>
        
        <div class="form-actions">
          <button type="submit" class="btn btn-dark">
            Entrar
          </button>
        </div>
      </form>
    </div>

    <div v-if="showErrorModal" class="modal" @click.self="showErrorModal = false">
      <div class="modal-content login-error-modal">
        <h2>Erro ao entrar</h2>
        <p>{{ errorMessage }}</p>
        <div class="modal-actions">
          <button type="button" class="btn btn-dark" @click="showErrorModal = false">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'

export default {
  name: 'Login',
  setup() {
    const store = useStore()
    const router = useRouter()
    
    const credentials = ref({
      usuario: localStorage.getItem('savedUser') || '',
      senha: ''
    })
    
    const salvarUsuario = ref(!!localStorage.getItem('savedUser'))
    const loginContainer = ref<HTMLElement | null>(null)
    const waveCanvas = ref<HTMLCanvasElement | null>(null)
    const showErrorModal = ref(false)
    const errorMessage = ref('')

    const getLoginErrorMessage = (error: unknown) => {
      if (typeof error === 'string' && error.trim()) {
        return error
      }

      if (error && typeof error === 'object') {
        const maybeError = error as { message?: unknown; error?: unknown }

        if (typeof maybeError.message === 'string' && maybeError.message.trim()) {
          return maybeError.message
        }

        if (typeof maybeError.error === 'string' && maybeError.error.trim()) {
          return maybeError.error
        }
      }

      return 'Erro ao fazer login. Tente novamente.'
    }

    let animationFrameId = 0
    let resizeHandler: (() => void) | null = null

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const resizeCanvas = () => {
      if (!waveCanvas.value || !loginContainer.value) return
      const rect = loginContainer.value.getBoundingClientRect()
      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      waveCanvas.value.width = Math.floor(rect.width * ratio)
      waveCanvas.value.height = Math.floor(rect.height * ratio)
      waveCanvas.value.style.width = `${rect.width}px`
      waveCanvas.value.style.height = `${rect.height}px`
    }

    const drawWaves = (time: number) => {
      if (!waveCanvas.value) return
      const ctx = waveCanvas.value.getContext('2d')
      if (!ctx) return

      const ratio = Math.min(window.devicePixelRatio || 1, 2)
      const width = waveCanvas.value.width / ratio
      const height = waveCanvas.value.height / ratio
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)

      const gradient = ctx.createLinearGradient(0, 0, 0, height)
      gradient.addColorStop(0, 'rgba(3, 13, 33, 0.96)')
      gradient.addColorStop(0.5, 'rgba(7, 28, 64, 0.92)')
      gradient.addColorStop(1, 'rgba(2, 10, 24, 0.97)')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, width, height)

      const driftX = (0.5 + Math.sin(time * 0.00025) * 0.22) * width
      const driftY = (0.48 + Math.cos(time * 0.00019) * 0.16) * height
      const glow = ctx.createRadialGradient(
        driftX,
        driftY,
        0,
        driftX,
        driftY,
        Math.max(width, height) * 0.4
      )
      glow.addColorStop(0, 'rgba(50, 175, 255, 0.26)')
      glow.addColorStop(0.45, 'rgba(28, 110, 230, 0.2)')
      glow.addColorStop(1, 'rgba(6, 22, 50, 0)')
      ctx.fillStyle = glow
      ctx.fillRect(0, 0, width, height)

      const bands = [
        { y: 0.18, a1: 20, a2: 10, f1: 125, f2: 60, s: 0.0011, color: '120, 210, 255', alpha: 0.44 },
        { y: 0.36, a1: 24, a2: 12, f1: 150, f2: 78, s: 0.00088, color: '100, 182, 255', alpha: 0.4 },
        { y: 0.58, a1: 22, a2: 9, f1: 136, f2: 72, s: 0.00126, color: '76, 160, 255', alpha: 0.36 },
        { y: 0.77, a1: 18, a2: 8, f1: 110, f2: 55, s: 0.00136, color: '58, 142, 255', alpha: 0.28 }
      ]

      for (let i = 0; i < bands.length; i += 1) {
        const band = bands[i]
        const baseY = height * band.y
        ctx.beginPath()
        ctx.moveTo(0, baseY)
        for (let x = 0; x <= width; x += 4) {
          const phase = time * band.s + i * 0.8
          const y =
            baseY +
            Math.sin(x / band.f1 + phase) * band.a1 +
            Math.cos(x / band.f2 - phase * 1.24) * band.a2 +
            Math.sin(x * 0.03 + time * 0.002 + i) * 7
          ctx.lineTo(x, y)
        }
        ctx.strokeStyle = `rgba(${band.color}, ${band.alpha})`
        ctx.lineWidth = 1.7 + i * 0.42
        ctx.shadowColor = `rgba(${band.color}, 0.22)`
        ctx.shadowBlur = 14
        ctx.stroke()
      }

      if (!reducedMotion) {
        animationFrameId = window.requestAnimationFrame(drawWaves)
      }
    }
    
    const handleLogin = async () => {
      showErrorModal.value = false
      errorMessage.value = ''

      try {
        const result = await store.dispatch('login', {
          email: credentials.value.usuario,
          password: credentials.value.senha
        })
        
        if (result.success) {
          if (salvarUsuario.value) {
            localStorage.setItem('savedUser', credentials.value.usuario)
          } else {
            localStorage.removeItem('savedUser')
          }
          router.push('/dashboard')
        } else {
          errorMessage.value = getLoginErrorMessage(result.error)
          showErrorModal.value = true
        }
      } catch (error: unknown) {
        errorMessage.value = getLoginErrorMessage(error)
        showErrorModal.value = true
      }
    }
    
    onMounted(() => {
      resizeCanvas()
      if (reducedMotion) {
        drawWaves(performance.now())
      } else {
        animationFrameId = window.requestAnimationFrame(drawWaves)
      }

      resizeHandler = () => {
        resizeCanvas()
      }
      window.addEventListener('resize', resizeHandler)
    })

    onBeforeUnmount(() => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId)
      }
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler)
      }
    })

    return {
      credentials,
      salvarUsuario,
      showErrorModal,
      errorMessage,
      handleLogin,
      loginContainer,
      waveCanvas
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background: linear-gradient(145deg, #010615 0%, #041733 52%, #0b2f5f 100%);
  padding: 40px 40px 40px 120px;
  position: relative;
  overflow: hidden;
}

.bg-wave-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.login-container::after {
  content: '';
  position: absolute;
  inset: 0;
  background:
    linear-gradient(rgba(122, 170, 255, 0.11) 1px, transparent 1px),
    linear-gradient(90deg, rgba(122, 170, 255, 0.11) 1px, transparent 1px);
  background-size: 38px 38px, 38px 38px;
  mix-blend-mode: screen;
  opacity: 0.34;
  animation: gridShift 20s linear infinite;
  pointer-events: none;
}

.login-container::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 22% 20%, rgba(102, 198, 255, 0.22), transparent 45%),
    radial-gradient(circle at 83% 72%, rgba(77, 136, 255, 0.2), transparent 40%);
  filter: blur(10px);
  pointer-events: none;
  z-index: 0;
}

.login-card {
  background: linear-gradient(160deg, rgba(248, 252, 255, 0.98), rgba(230, 242, 255, 0.95));
  padding: 30px;
  border-radius: 15px;
  border: 1px solid rgba(74, 145, 255, 0.3);
  box-shadow: 0 16px 42px rgba(0, 25, 70, 0.45);
  width: 100%;
  max-width: 320px;
  text-align: center;
  min-height: 450px;
  position: relative;
  z-index: 1;
  animation: cardEnter 650ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.brand-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  opacity: 0;
  animation: fadeInUp 500ms ease-out 70ms forwards;
}

.brand-logo {
  width: 440px;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 18px rgba(14, 89, 255, 0.35));
}

.logo-section {
  margin-bottom: 30px;
  opacity: 0;
  animation: fadeInUp 500ms ease-out 120ms forwards;
}

.login-card h1 {
  color: #1a237e;
  margin-bottom: 8px;
  font-size: 32px;
  font-weight: bold;
}

.slogan {
  color: #2f4f84;
  margin-bottom: 0;
  font-size: 14px;
  font-style: italic;
}

.login-form {
  text-align: left;
}

.form-group {
  margin-bottom: 20px;
  opacity: 0;
  animation: fadeInUp 500ms ease-out forwards;
}

.form-group:nth-of-type(1) {
  animation-delay: 200ms;
}

.form-group:nth-of-type(2) {
  animation-delay: 280ms;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #333;
  font-weight: 500;
}

.form-group input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
  box-sizing: border-box;
  transition: border-color 220ms ease, box-shadow 220ms ease, transform 220ms ease;
}

.form-group input:focus {
  outline: none;
  border-color: #2f72ff;
  box-shadow: 0 0 0 4px rgba(47, 114, 255, 0.15);
  transform: translateY(-1px);
}

.checkbox-group {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  opacity: 0;
  animation: fadeInUp 500ms ease-out 360ms forwards;
}

.checkbox-group input[type="checkbox"] {
  margin-right: 8px;
  width: auto;
  transform: scale(1.2);
  cursor: pointer;
}

.checkbox-group label {
  color: #333;
  font-size: 14px;
  margin-bottom: 0;
  cursor: pointer;
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
  opacity: 0;
  animation: fadeInUp 500ms ease-out 430ms forwards;
}

.login-error-modal {
  text-align: left;
  border: 1px solid rgba(74, 145, 255, 0.25);
  box-shadow: 0 16px 42px rgba(0, 25, 70, 0.28);
}

.login-error-modal h2 {
  color: #1a237e;
  margin-bottom: 12px;
}

.login-error-modal p {
  color: #333;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-dark {
  background: linear-gradient(130deg, #0d42c9, #1677ff);
  color: white;
  transform: translateY(0);
  box-shadow: 0 10px 20px rgba(13, 66, 201, 0.32);
}

.btn-dark:hover {
  background: linear-gradient(130deg, #0c37a7, #0f62d9);
  transform: translateY(-1px);
  box-shadow: 0 14px 26px rgba(12, 55, 167, 0.4);
}

.btn-dark:active {
  transform: translateY(0);
  box-shadow: 0 7px 14px rgba(12, 55, 167, 0.3);
}

.btn-blue {
  background-color: #2196f3;
  color: white;
}

.btn-blue:hover {
  background-color: #1976d2;
}

.btn-green {
  background-color: #4caf50;
  color: white;
}

.btn-green:hover {
  background-color: #388e3c;
}

.version {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.version p {
  color: #666;
  font-size: 12px;
  margin: 0;
}

@keyframes cardEnter {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes gridShift {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(38px, 38px, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .login-card,
  .brand-wrap,
  .logo-section,
  .form-group,
  .checkbox-group,
  .form-actions,
  .login-container::after {
    animation: none !important;
  }

  .btn,
  .form-group input {
    transition: none !important;
  }
}

@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    padding: 20px;
  }

  .login-container::after {
    opacity: 0.22;
  }
}
</style>



